import { lazy } from "react";

// Cada deploy borra del origen los chunks con hash del build anterior. Una pestana
// que arranco con un index.html cacheado sigue pidiendo esos hashes: el bundle
// principal le sale de su cache local, asi que la app parece sana, pero el primer
// chunk lazy que no tenga cacheado (tipicamente una ruta que visita poco, /admin)
// devuelve 404 y React.lazy rechaza -> ErrorBoundary con "Loading chunk N failed".
// El unico arreglo real es recargar: el HTML fresco trae los hashes nuevos.
const RELOAD_FLAG = "qc_chunk_reload";

function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch { return undefined; }
}

function ssSet(key, value) {
    try { window.sessionStorage.setItem(key, value); return true; } catch { return false; }
}

function ssDel(key) {
    try { window.sessionStorage.removeItem(key); } catch { /* nada que limpiar */ }
}

function isChunkError(err) {
    if (!err) return false;
    if (err.name === "ChunkLoadError") return true;
    return /loading chunk|loading css chunk|dynamically imported module|failed to fetch/i.test(err.message || "");
}

// El SW cachea estaticos cache-first, asi que puede tener clavados assets del build
// viejo. Best effort: si falla, la recarga sigue siendo lo que arregla el problema.
async function dropCaches() {
    try {
        if (typeof caches === "undefined") return;
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
    } catch { /* best effort */ }
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Exportada aparte de lazyRoute para poder testear la decision sin montar React.
export async function loadWithRecovery(factory, retryDelay = 500) {
    try {
        const mod = await factory();
        ssDel(RELOAD_FLAG);
        return mod;
    } catch (err) {
        // Un error de ejecucion del propio modulo no se arregla recargando.
        if (!isChunkError(err)) throw err;

        // Un corte puntual de red si: webpack 5 no memoiza el chunk fallido, asi
        // que reintentar vuelve a pedirlo de verdad.
        try {
            await wait(retryDelay);
            const mod = await factory();
            ssDel(RELOAD_FLAG);
            return mod;
        } catch { /* build caducado: seguimos con la recarga */ }

        // Sin guardia fiable no recargamos: preferimos el ErrorBoundary a un bucle
        // infinito de recargas.
        if (ssGet(RELOAD_FLAG) === "1") throw err;
        if (!ssSet(RELOAD_FLAG, "1")) throw err;

        await dropCaches();
        window.location.reload();
        // La pestana se esta yendo: dejamos Suspense en carga en vez de enseñar el
        // error un instante antes de la recarga.
        return new Promise(() => {});
    }
}

export default function lazyRoute(factory) {
    return lazy(() => loadWithRecovery(factory));
}
